'use strict';

Object.defineProperty(exports, '__esModule', { value: true });

const defaults = {
    initialisedClass: '',
    activePanelClass: '',
    activeHeaderClass: '',
    headerButtonClass: '',
    animate: true,
    multiselect: true,
    prefix: 'accordion',
};

/**
 * Wraps a number at a given value.
 *
 * @param x Value to wrap
 * @param n The maximum value
 */
function modulo(x, n) {
    return ((x % n) + n) % n;
}

function animateOpen(element, animate = true) {
    if (!animate || !hasHeightTransition(element)) {
        element.style.height = '';
        element.style.display = 'block';
    }
    else {
        let startHeight = element.clientHeight;
        element.style.height = 'auto';
        element.style.display = 'block';
        let endHeight = element.clientHeight;
        animateHeight(element, startHeight, endHeight);
    }
}
function animateClose(element, animate = true) {
    if (!animate || !hasHeightTransition(element)) {
        element.style.display = 'none';
    }
    else {
        animateHeight(element, getInnerHeight(element), 0);
    }
}
/**
 * Test if an Element has a height transition with a non zero duration.
 *
 * A panel may not have a CSS height transition set or its duration may be
 * zero. In these scenarios either the transitionEnd event is not fired,
 * event.propertyName is never equal to ‘height’, or the event may be fired
 * before we can catch it. We therefore need to check that the panel
 * will animate.
 */
function hasHeightTransition(element) {
    let style = window.getComputedStyle(element);
    let property = style.getPropertyValue('transition-property');
    let duration = parseFloat(style.getPropertyValue('transition-duration'));
    return duration > 0 && ['all', 'height'].includes(property);
}
/**
 * Animates the height of an element between two values.
 *
 * @param element - The element to animate.
 * @param start - The start height of the element.
 * @param end - The end height of the element.
 */
function animateHeight(element, start, end) {
    window.requestAnimationFrame(() => {
        element.style.height = `${start}px`;
        window.requestAnimationFrame(() => {
            element.style.height = `${end}px`;
            element.addEventListener('transitionend', onTransitionend);
        });
    });
}
function onTransitionend(event) {
    if (event.propertyName !== 'height') {
        return;
    }
    let element = event.currentTarget;
    if (!(element instanceof HTMLElement)) {
        return;
    }
    if (getInnerHeight(element) === 0) {
        element.style.display = 'none';
    }
    element.style.height = '';
    element.removeEventListener('transitionend', onTransitionend);
}
/**
 * Gets the height of an element, including padding.
 *
 * @param element
 * @returns The elements height
 */
function getInnerHeight(element) {
    let style = window.getComputedStyle(element);
    let height = parseInt(style.getPropertyValue('height'), 10);
    let paddingTop = parseInt(style.getPropertyValue('padding-top'), 10);
    let paddingBottom = parseInt(style.getPropertyValue('padding-bottom'), 10);
    return height - paddingTop - paddingBottom;
}

const instances = {};
function getInstanceById(id) {
    if (!(Object.prototype.hasOwnProperty.call(instances, id))) {
        return undefined;
    }
    return instances[id];
}
/**
 * @param id - The ID of the containing HTMLElement.
 * @param options
 */
function accordion(id, options = {}) {
    const config = { ...defaults, ...options };
    const rootElement = document.getElementById(id);
    if (rootElement === null) {
        return;
    }
    if (getInstanceById(id)) {
        return getInstanceById(id);
    }
    let initialised = false;
    let activeIds = [];
    let items = [];
    let events = {};
    function getItemIndex(target) {
        let index;
        if (typeof target === 'number') {
            index = target;
        }
        else {
            index = items.findIndex(item => item.id === target);
        }
        return index;
    }
    function getItem(target) {
        let index = getItemIndex(target);
        if (index === -1) {
            return undefined;
        }
        return items[index];
    }
    function init() {
        if (initialised) {
            return;
        }
        const headers = Array.from(rootElement.querySelectorAll(`[${getPrefixedDataAttribute('for')}]`));
        headers.forEach(header => {
            if (!(header instanceof HTMLElement)) {
                return;
            }
            let item = addItem(header);
            if (item === undefined) {
                return;
            }
            if (header.getAttribute(getPrefixedDataAttribute('expanded')) !== null) {
                item.open({ animate: false });
            }
            if (header.getAttribute(getPrefixedDataAttribute('disabled')) !== null) {
                item.disable();
            }
        });
        initialised = true;
        if (config.initialisedClass) {
            rootElement.classList.add(config.initialisedClass);
        }
        emit('init');
    }
    function addItem(element) {
        let id = element.getAttribute(getPrefixedDataAttribute('for'));
        if (id === undefined) {
            return;
        }
        let panel = document.getElementById(id);
        let button = document.createElement('button');
        if (panel === null) {
            return;
        }
        button.type = 'button';
        button.id = `${id}-label`;
        if (config.headerButtonClass) {
            button.className = config.headerButtonClass;
        }
        button.innerHTML = element.innerHTML;
        element.innerHTML = '';
        element.appendChild(button);
        button.setAttribute('aria-controls', id);
        button.setAttribute('aria-expanded', 'false');
        button.addEventListener('click', event => onHeaderClick(event, id));
        button.addEventListener('keydown', event => onHeaderKeydown(event, id));
        panel.style.display = 'none';
        panel.setAttribute('role', 'region');
        panel.setAttribute('aria-labelledby', `${id}-label`);
        let item = {
            id,
            panel,
            button,
            header: element,
            active: false,
            disabled: false,
            enable: () => enable(id),
            disable: () => disable(id),
            open: (options = {}) => _expand(id, options),
            close: (options = {}) => _collapse(id, options),
        };
        items.push(item);
        return item;
    }
    function destroy() {
        if (!initialised) {
            return;
        }
        if (config.initialisedClass) {
            rootElement.classList.remove(config.initialisedClass);
        }
        items.forEach(item => {
            let buttonHtml = item.button.innerHTML;
            let buttonParent = item.button.parentNode;
            let { activePanelClass, activeHeaderClass } = config;
            if (activeHeaderClass) {
                item.button.classList.remove(activeHeaderClass);
            }
            if (activePanelClass) {
                item.panel.classList.remove(activePanelClass);
            }
            item.button.remove();
            buttonParent.innerHTML = buttonHtml;
            ['role', 'style', 'aria-labelledby'].forEach(attribute => {
                item.panel.removeAttribute(attribute);
            });
        });
        items.length = 0;
        activeIds.length = 0;
        delete instances[id];
        emit('destroy');
        for (let [name, handlers] of Object.entries(events)) {
            handlers.forEach(handler => off(name, handler));
            delete events[name];
        }
        initialised = false;
    }
    function moveFocus(index, relative = false) {
        let newIndex;
        if (!relative) {
            newIndex = index;
        }
        else {
            let active = document.activeElement;
            let activeIndex = items.findIndex(item => item.button === active);
            if (activeIndex === -1) {
                return false;
            }
            newIndex = activeIndex + index;
        }
        items[modulo(newIndex, items.length)].button.focus();
    }
    function open(target, options = {}) {
        if (!Array.isArray(target)) {
            _expand(target, options);
        }
        else {
            target.forEach(id => _expand(id, options));
        }
    }
    function close(target, options = {}) {
        if (!Array.isArray(target)) {
            _collapse(target, options);
        }
        else {
            target.forEach(id => _collapse(id, options));
        }
    }
    function toggle(target, options = {}) {
        let item = getItem(target);
        if (item === undefined) {
            return;
        }
        item.active ? item.close(options) : item.open(options);
    }
    function openAll() {
        items.forEach(item => item.open({ animate: false, multiselect: true }));
    }
    function closeAll() {
        items.forEach(item => item.close({ animate: false }));
    }
    function enable(target) {
        let item = getItem(target);
        if (item === undefined) {
            return;
        }
        if (!emit('enable', { cancelable: true, detail: item })) {
            return;
        }
        item.disabled = false;
        item.button.setAttribute('aria-disabled', 'false');
    }
    function disable(target) {
        let item = getItem(target);
        if (item === undefined) {
            return;
        }
        if (!emit('disable', { cancelable: true, detail: item })) {
            return;
        }
        item.disabled = true;
        item.button.setAttribute('aria-disabled', 'true');
    }
    function _expand(target, { animate = config.animate, multiselect = config.multiselect } = {}) {
        let item = getItem(target);
        if (item === undefined || item.active || item.disabled) {
            return;
        }
        if (!emit('open', { cancelable: true, detail: item })) {
            return;
        }
        item.active = true;
        item.button.setAttribute('aria-expanded', 'true');
        let { activePanelClass, activeHeaderClass } = config;
        if (activePanelClass) {
            item.panel.classList.add(activePanelClass);
        }
        if (activeHeaderClass) {
            item.header.classList.add(activeHeaderClass);
        }
        animateOpen(item.panel, animate);
        if (!multiselect) {
            close(activeIds, options);
        }
        activeIds.push(item.id);
    }
    function _collapse(target, { animate = config.animate } = {}) {
        let item = getItem(target);
        if (item === undefined || !item.active || item.disabled) {
            return;
        }
        if (!emit('close', { cancelable: true, detail: item })) {
            return;
        }
        item.active = false;
        item.button.setAttribute('aria-expanded', 'false');
        let { activePanelClass, activeHeaderClass } = config;
        if (activePanelClass) {
            item.panel.classList.remove(activePanelClass);
        }
        if (activeHeaderClass) {
            item.header.classList.remove(activeHeaderClass);
        }
        animateClose(item.panel, animate);
        let index = activeIds.indexOf(item.id);
        if (index > -1) {
            activeIds.splice(index, 1);
        }
    }
    function onHeaderClick(event, id) {
        toggle(id);
        event.preventDefault();
    }
    function onHeaderKeydown(event, id) {
        let { key } = event;
        let keys = {
            'ArrowUp': () => moveFocus(-1, true),
            'ArrowDown': () => moveFocus(1, true),
            'End': () => moveFocus(items.length - 1),
            'Home': () => moveFocus(0),
            'Enter': () => toggle(id),
            'Space': () => toggle(id),
        };
        if (!Object.prototype.hasOwnProperty.call(keys, key)) {
            return;
        }
        keys[key]();
        event.preventDefault();
    }
    let on = function (type, listener) {
        let prefixedName = getPrefixedEventName(type);
        rootElement.addEventListener(prefixedName, listener);
        // Track event listeners to remove when calling destroy.
        (events[prefixedName] || (events[prefixedName] = [])).push(listener);
    };
    let off = function (type, listener) {
        rootElement.removeEventListener(getPrefixedEventName(type), listener);
    };
    /**
     * Dispatches an event.
     *
     * @param type - The event type.
     * @param options
     * @returns False if preventDefault() was called, true otherwise.
     */
    function emit(type, options = {}) {
        let defaults = {
            bubbles: true,
            cancelable: false,
        };
        let event = new CustomEvent(getPrefixedEventName(type), {
            ...defaults,
            ...options,
        });
        return rootElement.dispatchEvent(event);
    }
    /**
     * Returns the given event name with the event prefix.
     *
     * @param name The event name
     * @returns The prefixed event name
     */
    function getPrefixedEventName(name) {
        return `${config.prefix}:${name};`;
    }
    function getPrefixedDataAttribute(name) {
        return `data-${config.prefix}-${name}`;
    }
    return instances[id] = {
        id,
        root: rootElement,
        on,
        off,
        init,
        destroy,
        open,
        close,
        toggle,
        enable,
        disable,
        openAll,
        closeAll,
        getItem,
        items,
    };
}

exports.accordion = accordion;
exports.getInstanceById = getInstanceById;
exports.instances = instances;
//# sourceMappingURL=accordion.js.map
